/**
 * "Prices captured" and "Compare with" of "My office network" (CU-869fmjzrh D5, P1, decision 9), and the options that
 * do not fit the bounds of the server, disabled with their reason (`patterns.md` § 5), never removed. Pure, alias-free.
 */
import { addDays, daysBetween, startOfDay } from '../../../@services/dates.js'

export const MAX_CAPTURE_DAYS = 31
export const OFFICE_DAYS_MAX = 400
export const MAX_CELLS = 6000

export const CAPTURES = Object.freeze({
	yday: { label: 'Yesterday' },
	today: { label: 'Today, so far' },
	last7: { label: 'Last 7 days' },
	fixed: { label: 'Fixed dates' },
})

export const COMPARES = Object.freeze({
	day: { label: 'Day before', periodDays: 1 },
	week: { label: 'Week before', periodDays: 7 },
	year: { label: 'Year before' },
	yearSame: { label: 'Year before, same date' },
})

/** What the server calls each comparison */
export const COMPARE_BODY = Object.freeze({ day: 'day', week: 'week', year: 'year', yearSame: 'yearSameDate' })

export function isValidCapture(fixed) {
	if (!(fixed?.from instanceof Date) || !(fixed?.to instanceof Date)) return false
	if (Number.isNaN(fixed.from.getTime()) || Number.isNaN(fixed.to.getTime())) return false
	const days = daysBetween(fixed.from, fixed.to) + 1
	return days >= 1 && days <= MAX_CAPTURE_DAYS
}

/** `{ from, to, days }` of a capture key; `fixed` needs `{ from, to }`; `null` when it cannot be resolved */
export function resolveCapture(key, today, fixed = null) {
	const t = startOfDay(today)
	const y = addDays(t, -1)
	const out = (from, to) => ({ from, to, days: daysBetween(from, to) + 1 })
	switch (key) {
		case 'yday': return out(y, y)
		case 'today': return out(t, t)
		case 'last7': return out(addDays(y, -6), y)
		case 'fixed': return isValidCapture(fixed) ? out(startOfDay(fixed.from), startOfDay(fixed.to)) : null
		default: return null
	}
}

/** The most offices a capture of `days` allows (the server's cost bound, per period) */
export const officesFor = days => Math.max(Math.floor(OFFICE_DAYS_MAX / Math.max(days, 1)), 1)

/** The most capture days a network of `offices` allows: 31 (decision 9), fewer for a large network (the cost bound) */
export const maxCaptureDays = offices => Math.min(MAX_CAPTURE_DAYS, Math.max(Math.floor(OFFICE_DAYS_MAX / Math.max(offices || 1, 1)), 1))

/** Options of "Prices captured" with the network size: `[{ value, label, disabled, reason }]` */
export function captureOptions(networkOffices = 0) {
	return Object.entries(CAPTURES).map(([value, { label }]) => {
		const most = officesFor(7)
		const disabled = value === 'last7' && networkOffices > most
		return { value, label, disabled, reason: disabled ? `Up to ${most} offices: choose a country or a group (you have ${networkOffices})` : null }
	})
}

/** Options of "Compare with" for a capture of `days` (P1): a window that would overlap is disabled with its reason */
export function compareOptions(days = 1) {
	return Object.entries(COMPARES).map(([value, { label, periodDays }]) => {
		const disabled = !!periodDays && days > periodDays
		const reason = !disabled ? null : periodDays === 1 ? 'Only with one capture day: these windows would overlap' : 'Only with up to 7 capture days'
		return { value, label, disabled, reason }
	})
}

/** A comparison that the capture no longer allows falls back to the first that it does */
export function fitCompare(cmp, days) {
	const opts = compareOptions(days)
	return opts.find(o => o.value === cmp && !o.disabled) ? cmp : (opts.find(o => !o.disabled) || opts[2]).value
}

/** Day columns in the pick-up view: disabled when offices × days does not fit (UX T4) */
export function dayColumnsFit(offices, pickupDays) {
	return offices * pickupDays <= MAX_CELLS
}

/** The capture a link or an arrival asks, cut to what the bound allows: `{ from, to, cut }` */
export function fitCapture({ from, to }, offices) {
	const most = Math.min(MAX_CAPTURE_DAYS, Math.max(Math.floor(OFFICE_DAYS_MAX / Math.max(offices, 1)), 1))
	const days = daysBetween(from, to) + 1
	if (days <= most) return { from, to, cut: false }
	return { from: addDays(to, -(most - 1)), to, cut: true, days: most, asked: days }
}

/**
 * What the first requests ask while the size of the network is unknown, and what the view becomes once it is (QA 2-3):
 *  - the pick-up view never asks Day until offices × days is known to fit; it asks Week meanwhile, and when Day does
 *    not fit the view itself becomes Week (`persistBy`), so a reload, a copied link or a Leading arrival asks Week;
 *  - a link with fixed capture dates (`fitPending`) asks only its last day until the size is known, then the capture is
 *    trimmed to what the cost bound allows (`fit`: `fitCapture`'s answer), never a 400.
 * `networkSize` 0 = unknown.
 */
export function requestPlan({ v, by, pickupDays, capture, networkSize = 0, fitPending = false }) {
	const known = networkSize > 0
	const dayFits = known && dayColumnsFit(networkSize, pickupDays)
	const askDay = v === 'pickup' && by === 'day'
	const plan = {
		by: askDay && !dayFits ? 'week' : by,
		persistBy: askDay && known && !dayFits ? 'week' : null,
		capture,
		fit: null,
	}
	if (fitPending && capture && capture.days > 1) {
		if (!known) plan.capture = { from: capture.to, to: capture.to, days: 1 }
		else {
			plan.fit = { ...fitCapture(capture, networkSize), asked: capture.days }
			plan.fit.days = daysBetween(plan.fit.from, plan.fit.to) + 1
			plan.capture = { from: plan.fit.from, to: plan.fit.to, days: plan.fit.days }
		}
	}
	return plan
}

/**
 * The size of the network belongs to its office filter (QA re-check): remembered per filter, never the last view's for
 * another one. Explicit offices are their own count, known at once (facets can only make it smaller), so a one-office
 * link arrives untouched; a group or another filter is unknown (0) until its first answer.
 */
export function officeFilterKey(view = {}) {
	const sorted = xs => [...(xs || [])].map(String).sort().join(',')
	return [sorted(view.countries), sorted(view.types), sorted(view.offices), view.officeGroup || ''].join('|')
}

export function sizeFor(sizes = {}, view = {}) {
	if (view.offices?.length && !view.officeGroup) return view.offices.length
	return Number((sizes || {})[officeFilterKey(view)]) || 0
}

export function rememberSize(sizes = {}, view = {}, size) {
	return { ...(sizes || {}), [officeFilterKey(view)]: size }
}
